import { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash';

import { useMediaPredicate } from 'react-media-hook';
import { MOBILE_SIZE } from 'utils/mediaQueryUtils';

import StandardModal from 'modals/StandardModal';

import { usePageTitle } from 'utils/pageTitle';
import { isAdminUser } from 'utils/userUtils';
import { actions } from 'data/store';
import { dataConfig } from 'data/dataConfig';
import { prettifyDataSource } from 'data/dataSourceConstants';
import {
	groupJobApplications,
	filterJobApplicationGroups,
	countJobApplicationGroups,
	paginateGroups,
} from 'utils/jobApplicationStatusUtils';
import {
	groupDobNowJobApplications,
	setDobNowClosedStatuses,
	filterDobNowJobApplicationGroups,
	countDobNowJobApplicationGroups,
	paginateDobNowGroups,
} from 'utils/dobNowJobApplicationStatusUtils';
import DataTable from 'components/dataTable/DataTable';
import JobApplicationsGroupedTable from 'components/dataTable/JobApplicationsGroupedTable';
import DobNowJobApplicationsGroupedTable from 'components/dataTable/DobNowJobApplicationsGroupedTable';
import FilterButtons from 'components/dataTable/FilterButtons';
import OwnerFilterDropdown from 'components/dataTable/OwnerFilterDropdown';
import PaginatorV3 from 'components/Paginator/PaginatorV3';
import Spinner from 'components/Spinner';
import OutboundLink from 'components/OutboundLink';

const JOB_APPLICATIONS_PER_PAGE = 500;
const JOB_APPLICATION_GROUPS_PER_PAGE = 20;
const DOB_NOW_APPLICATIONS_PER_PAGE = 500;
const DOB_NOW_GROUPS_PER_PAGE = 20;

const DataTableModal = ({ options }) => {
	const { dataSet, reducerName, dataSource, title, columns, filteredStatus: initialFilteredStatus, tableView } = options;
	const dispatch = useDispatch();
	const isMobile = useMediaPredicate(MOBILE_SIZE);
	let {
		propertyId,
		isLoading,
		data,
		currentPage,
		totalPages,
		filteredStatus,
		sortField,
		sortDirection,
		numOpen,
		numClosed,
		numCombined,
		dataSetUrl,
		propertyData,
		reduxSearchString,
		closedStatuses,
		user,
	} = useSelector((state) => ({
		propertyId: state.property.propertyData?.id,
		isLoading:
			state[reducerName].isLoading ||
			state.property.isLoading ||
			state.address.isLoading,
		data: state[reducerName].data,
		currentPage: state[reducerName].currentPage,
		totalPages: state[reducerName].totalPages,
		filteredStatus: state[reducerName].filteredStatus,
		sortField: state[reducerName].sortField,
		sortDirection: state[reducerName].sortDirection,
		numOpen: state[reducerName].numOpen,
		numClosed: state[reducerName].numClosed,
		numCombined: state[reducerName]?.numCombined || 0,
		dataSetUrl: state[reducerName].dataSetUrl,
		propertyData: state.property.propertyData,
		reduxSearchString: state[reducerName].searchString || '',
		closedStatuses: state[reducerName]?.closedStatuses || [],
		user: state.user.userData,
	}));

	usePageTitle(title, propertyData);
	// Manage loading overlay with smooth transitions
	const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

	useEffect(() => {
		setShowLoadingOverlay(isLoading);
	}, [isLoading]);

	let [searchString, setSearchString] = useState('');
	let [ownerFilter, setOwnerFilter] = useState('all');
	let [groupPage, setGroupPage] = useState(1);
	let [groupFilteredStatus, setGroupFilteredStatus] = useState(initialFilteredStatus || 'open');
	const isFdnyPermits2 = dataSet === 'fdnyPermits2';
	const isAdmin = isAdminUser(user);
	const isJobApplications = dataSet === 'jobApplications';
	const isLegacyJobApplications = isJobApplications && tableView === 'legacy' && isAdmin;
	const isGroupedJobApplications = isJobApplications && !isLegacyJobApplications;
	const isDobJobApplications = dataSet === 'dobJobApplications';
	const statusForFetch = isFdnyPermits2 ? 'all' : filteredStatus;

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		if (!propertyId) return;

		if (isGroupedJobApplications) {
			dispatch(
				actions[reducerName].fetchData(
					propertyId,
					'all',
					1,
					reduxSearchString,
					{},
					JOB_APPLICATIONS_PER_PAGE
				)
			);
			return;
		}

		if (isDobJobApplications) {
			dispatch(
				actions[reducerName].fetchData(
					propertyId,
					'all',
					1,
					reduxSearchString,
					{},
					DOB_NOW_APPLICATIONS_PER_PAGE
				)
			);
			return;
		}

		if (isLegacyJobApplications) {
			dispatch(actions[reducerName].fetchData(propertyId, 'all', 1, reduxSearchString));
			return;
		}

		const statusToFetch = isFdnyPermits2 ? 'all' : (initialFilteredStatus || 'all');
		dispatch(actions[reducerName].fetchData(propertyId, statusToFetch));
	}, [propertyId, reducerName, reduxSearchString, tableView]);

	useEffect(() => {
		setGroupPage(1);
	}, [dataSet, dataSource, groupFilteredStatus, searchString, data]);

	let subtitle = `(${prettifyDataSource(dataSource)})`;
	let showStatusFilter = dataConfig[dataSet].hasStatus !== false && !isFdnyPermits2;
	const openLabel = isFdnyPermits2 ? 'Expired' : (dataSet === 'bisIlluminatedSigns' ? 'Expired' : 'Open');
	const closedLabel = isFdnyPermits2 ? 'Active' : (dataSet === 'bisIlluminatedSigns' ? 'Active' : 'Resolved');

	const ownerOptions = useMemo(() => {
		if (!isFdnyPermits2 || !Array.isArray(data)) {
			return [];
		}
		const normalizedToLabel = new Map();
		data.forEach((row) => {
			const ownerName = (row?.owner_name || '').trim();
			if (!ownerName) return;
			const normalized = ownerName.toLowerCase();
			if (!normalizedToLabel.has(normalized)) {
				normalizedToLabel.set(normalized, ownerName);
			}
		});
		return Array.from(normalizedToLabel.values()).sort((a, b) => a.localeCompare(b));
	}, [data, isFdnyPermits2]);

	useEffect(() => {
		if (!isFdnyPermits2) return;
		if (ownerFilter === 'all') return;
		if (!ownerOptions.includes(ownerFilter)) {
			setOwnerFilter('all');
		}
	}, [isFdnyPermits2, ownerFilter, ownerOptions]);

	const filteredData = useMemo(() => {
		if (!isFdnyPermits2 || ownerFilter === 'all') {
			return data;
		}
		if (!Array.isArray(data)) {
			return data;
		}
		const target = ownerFilter.trim().toLowerCase();
		return data.filter((row) => {
			const ownerName = (row?.owner_name || '').trim().toLowerCase();
			return ownerName === target;
		});
	}, [data, isFdnyPermits2, ownerFilter]);

	const jobApplicationGroups = useMemo(() => {
		if (!isGroupedJobApplications || !Array.isArray(data)) {
			return [];
		}
		return groupJobApplications(data, dataSource);
	}, [data, dataSource, isGroupedJobApplications]);

	const filteredJobApplicationGroups = useMemo(() => {
		if (!isGroupedJobApplications) {
			return [];
		}
		return filterJobApplicationGroups(jobApplicationGroups, groupFilteredStatus);
	}, [isGroupedJobApplications, jobApplicationGroups, groupFilteredStatus]);

	const jobApplicationCounts = useMemo(() => {
		if (!isGroupedJobApplications) {
			return { numOpen, numClosed, numCombined };
		}
		return countJobApplicationGroups(jobApplicationGroups);
	}, [isGroupedJobApplications, jobApplicationGroups, numOpen, numClosed, numCombined]);

	const paginatedJobApplicationGroups = useMemo(() => {
		if (!isGroupedJobApplications) {
			return { groups: [], currentPage: 1, totalPages: 1 };
		}
		return paginateGroups(
			filteredJobApplicationGroups,
			groupPage,
			JOB_APPLICATION_GROUPS_PER_PAGE
		);
	}, [isGroupedJobApplications, filteredJobApplicationGroups, groupPage]);

	const dobNowGroups = useMemo(() => {
		if (!isDobJobApplications || !Array.isArray(data)) {
			return [];
		}
		// Adopt the server's closed list before grouping, as the overview table does.
		// This view can render before that one has, so it cannot rely on the list
		// already being set.
		setDobNowClosedStatuses(closedStatuses);
		return groupDobNowJobApplications(data);
	}, [data, isDobJobApplications, closedStatuses]);

	const filteredDobNowGroups = useMemo(() => {
		if (!isDobJobApplications) {
			return [];
		}
		return filterDobNowJobApplicationGroups(dobNowGroups, groupFilteredStatus);
	}, [isDobJobApplications, dobNowGroups, groupFilteredStatus]);

	const dobNowCounts = useMemo(() => {
		if (!isDobJobApplications) {
			return { numOpen, numClosed, numCombined };
		}
		return countDobNowJobApplicationGroups(dobNowGroups);
	}, [isDobJobApplications, dobNowGroups, numOpen, numClosed, numCombined]);

	const paginatedDobNowGroups = useMemo(() => {
		if (!isDobJobApplications) {
			return { groups: [], currentPage: 1, totalPages: 1 };
		}
		return paginateDobNowGroups(filteredDobNowGroups, groupPage, DOB_NOW_GROUPS_PER_PAGE);
	}, [isDobJobApplications, filteredDobNowGroups, groupPage]);

	const debouncedSearch = debounce((searchValue) => {
		setSearchString(searchValue);
		if (isGroupedJobApplications) {
			dispatch(
				actions[reducerName].fetchData(
					propertyId,
					'all',
					1,
					searchValue,
					{},
					JOB_APPLICATIONS_PER_PAGE
				)
			);
			return;
		}
		if (isDobJobApplications) {
			dispatch(
				actions[reducerName].fetchData(
					propertyId,
					'all',
					1,
					searchValue,
					{},
					DOB_NOW_APPLICATIONS_PER_PAGE
				)
			);
			return;
		}
		if (isLegacyJobApplications) {
			dispatch(actions[reducerName].fetchData(propertyId, 'all', 1, searchValue));
			return;
		}
		dispatch(
			actions[reducerName].fetchData(propertyId, statusForFetch, 1, searchValue)
		);
	}, 400);

	const onSort = (nextSort) => {
		if (isGroupedJobApplications) {
			return dispatch(
				actions[reducerName].fetchData(
					propertyId,
					'all',
					1,
					searchString,
					nextSort,
					JOB_APPLICATIONS_PER_PAGE
				)
			);
		}
		if (isDobJobApplications) {
			return dispatch(
				actions[reducerName].fetchData(
					propertyId,
					'all',
					1,
					searchString,
					nextSort,
					DOB_NOW_APPLICATIONS_PER_PAGE
				)
			);
		}
		if (isLegacyJobApplications) {
			return dispatch(
				actions[reducerName].fetchData(propertyId, 'all', 1, searchString, nextSort)
			);
		}
		return dispatch(
			actions[reducerName].fetchData(propertyId, statusForFetch, 1, searchString, nextSort)
		);
	};

	return (
		<StandardModal
			title={`${title} ${subtitle}`}
			className="data-table-modal"
			size="max"
			closeOnOutsideClick
		>
			<div
				className={`data-table-container ${
					isMobile ? 'data-table-container-mobile' : ''
				}`}
			>
				<nav className="navbar navbar-expand-lg">
					<div className="container-fluid">
						<div>
							<span className="navbar-brand data-set-name">{title}</span>
							<span className="navbar-text data-source-name">
								<OutboundLink url={dataSetUrl}>{subtitle}</OutboundLink>
							</span>
						</div>
						<div className="table-controls">
							<input
								type="text"
								className="form-control data-table-search me-3"
								placeholder={`Search ${title}`}
								onChange={(e) => debouncedSearch(e.target.value)}
							/>
							{isFdnyPermits2 && (
								<OwnerFilterDropdown
									onFilter={(owner) => setOwnerFilter(owner)}
									filteredOwner={ownerFilter}
									owners={ownerOptions}
									isDisabled={isLoading}
								/>
							)}
							{showStatusFilter && !isGroupedJobApplications && !isDobJobApplications && (
								<FilterButtons
									onFilter={(status) =>
										dispatch(
											actions[reducerName].fetchData(
												propertyId,
												status,
												1,
												searchString
											)
										)
									}
									filteredStatus={filteredStatus}
									numOpen={numOpen}
									numClosed={numClosed}
									numCombined={numCombined}
									openLabel={openLabel}
									closedLabel={closedLabel}
								/>
							)}
							{(isGroupedJobApplications || isDobJobApplications) && (
								<FilterButtons
									onFilter={(status) => setGroupFilteredStatus(status)}
									filteredStatus={groupFilteredStatus}
									numOpen={
										isGroupedJobApplications
											? jobApplicationCounts.numOpen
											: dobNowCounts.numOpen
									}
									numClosed={
										isGroupedJobApplications
											? jobApplicationCounts.numClosed
											: dobNowCounts.numClosed
									}
									numCombined={
										isGroupedJobApplications
											? jobApplicationCounts.numCombined
											: dobNowCounts.numCombined
									}
									openLabel={openLabel}
									closedLabel={closedLabel}
								/>
							)}
						</div>
					</div>
				</nav>
				<div className="table-wrapper" style={{ position: 'relative' }}>
					{showLoadingOverlay && (
						<div
							className="loading-overlay"
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								backgroundColor: 'rgba(255, 255, 255, 0.9)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								zIndex: 10,
								transition: 'opacity 0.2s ease-in-out',
								opacity: showLoadingOverlay ? 1 : 0,
							}}
						>
							<Spinner />
						</div>
					)}
					{isGroupedJobApplications ? (
						<JobApplicationsGroupedTable
							columns={columns}
							groups={paginatedJobApplicationGroups.groups}
							dataSet={dataSet}
							dataSource={dataSource}
							propertyData={propertyData}
							isLoading={isLoading}
							searchString={searchString}
							sortField={sortField}
							sortDirection={sortDirection}
							onSort={onSort}
						/>
					) : isDobJobApplications ? (
						<DobNowJobApplicationsGroupedTable
							columns={columns}
							groups={paginatedDobNowGroups.groups}
							dataSet={dataSet}
							dataSource={dataSource}
							propertyData={propertyData}
							isLoading={isLoading}
							searchString={searchString}
							sortField={sortField}
							sortDirection={sortDirection}
							onSort={onSort}
						/>
					) : (
						<DataTable
							columns={columns}
							data={filteredData}
							dataSet={dataSet}
							dataSource={dataSource}
							propertyData={propertyData}
							isLoading={isLoading}
							searchString={searchString}
							sortField={sortField}
							sortDirection={sortDirection}
							onSort={onSort}
						/>
					)}
				</div>
				{/* violerts-v3-root gives PaginatorV3 its button reset, same as AlertsTable */}
				<div className="data-table-modal-paginator violerts-v3-root tw-flex tw-justify-center tw-pt-4">
					<PaginatorV3
						currentPage={
							(isGroupedJobApplications
								? paginatedJobApplicationGroups.currentPage
								: isDobJobApplications
									? paginatedDobNowGroups.currentPage
									: currentPage) || 1
						}
						totalPages={
							(isGroupedJobApplications
								? paginatedJobApplicationGroups.totalPages
								: isDobJobApplications
									? paginatedDobNowGroups.totalPages
									: totalPages) || 0
						}
						onPageChange={(pageNum) => {
							if (isGroupedJobApplications || isDobJobApplications) {
								setGroupPage(pageNum);
							} else {
								dispatch(actions[reducerName].setPage(pageNum));
							}
						}}
					/>
				</div>
			</div>
		</StandardModal>
	);
};

export default DataTableModal;
